import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, Download, Plus, Receipt, Building2, User, 
  TrendingUp, Calendar, RefreshCw, Lock, AlertCircle,
  FileSpreadsheet, IndianRupee
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GSTReports() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isReportingUser, setIsReportingUser] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  // Filters
  const [invoiceType, setInvoiceType] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // New Invoice Dialog
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    supply_type: 'intra',
    service_period_from: '',
    service_period_to: '',
    items: [{ description: 'Internet Service', hsn_code: '998314', quantity: 1, unit_price: 0 }]
  });
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Report generation
  const [reportType, setReportType] = useState('b2b');
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCustomers();
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchInvoices();
    }
  }, [token, invoiceType, fromDate, toDate]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API}/api/gst/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setIsReportingUser(data.is_reporting_user);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      let url = `${API}/api/gst/invoices?limit=100`;
      if (invoiceType !== 'all') url += `&invoice_type=${invoiceType}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setIsReportingUser(data.is_reporting_user);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const createInvoice = async () => {
    if (!newInvoice.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!newInvoice.items[0].unit_price) {
      toast.error('Please enter item price');
      return;
    }

    setCreatingInvoice(true);
    try {
      const response = await fetch(`${API}/api/gst/invoices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newInvoice)
      });

      if (response.ok) {
        toast.success('Invoice created successfully');
        setShowNewInvoice(false);
        setNewInvoice({
          customer_id: '',
          invoice_date: new Date().toISOString().split('T')[0],
          supply_type: 'intra',
          service_period_from: '',
          service_period_to: '',
          items: [{ description: 'Internet Service', hsn_code: '998314', quantity: 1, unit_price: 0 }]
        });
        fetchInvoices();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create invoice');
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const downloadReport = async (type) => {
    if (!reportFromDate || !reportToDate) {
      toast.error('Please select date range');
      return;
    }

    setGeneratingReport(true);
    try {
      const response = await fetch(
        `${API}/api/gst/reports/export/${type}?from_date=${reportFromDate}&to_date=${reportToDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GST_${type.toUpperCase()}_${reportFromDate}_to_${reportToDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else if (response.status === 403) {
        toast.error('Access denied. Report access is restricted.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="gst-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gst-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">GST Reports</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isReportingUser ? 'Full access to all GST data and reports' : 'Limited to recent invoices (last 7 days)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isReportingUser && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
              <Lock className="h-3 w-3 mr-1" />
              Restricted Access
            </Badge>
          )}
          <Dialog open={showNewInvoice} onOpenChange={setShowNewInvoice}>
            <DialogTrigger asChild>
              <Button data-testid="create-invoice-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create GST Invoice</DialogTitle>
                <DialogDescription>
                  Invoice type (B2B/B2C) is auto-determined based on customer GSTIN
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Customer</Label>
                  <Select
                    value={newInvoice.customer_id}
                    onValueChange={(val) => setNewInvoice({ ...newInvoice, customer_id: val })}
                  >
                    <SelectTrigger data-testid="select-customer">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          {c.company_name || c.contact_person} {c.gst_number ? `(GSTIN: ${c.gst_number})` : '(No GSTIN)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Invoice Date</Label>
                    <Input
                      type="date"
                      value={newInvoice.invoice_date}
                      onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })}
                      data-testid="invoice-date"
                    />
                  </div>
                  <div>
                    <Label>Supply Type</Label>
                    <Select
                      value={newInvoice.supply_type}
                      onValueChange={(val) => setNewInvoice({ ...newInvoice, supply_type: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="intra">Intra-State (CGST+SGST)</SelectItem>
                        <SelectItem value="inter">Inter-State (IGST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Service From</Label>
                    <Input
                      type="date"
                      value={newInvoice.service_period_from}
                      onChange={(e) => setNewInvoice({ ...newInvoice, service_period_from: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Service To</Label>
                    <Input
                      type="date"
                      value={newInvoice.service_period_to}
                      onChange={(e) => setNewInvoice({ ...newInvoice, service_period_to: e.target.value })}
                    />
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-slate-50">
                  <Label className="text-sm font-medium">Line Item</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-xs text-slate-500">Description</Label>
                      <Input
                        value={newInvoice.items[0].description}
                        onChange={(e) => setNewInvoice({
                          ...newInvoice,
                          items: [{ ...newInvoice.items[0], description: e.target.value }]
                        })}
                        placeholder="Service description"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">HSN Code</Label>
                      <Input
                        value={newInvoice.items[0].hsn_code}
                        onChange={(e) => setNewInvoice({
                          ...newInvoice,
                          items: [{ ...newInvoice.items[0], hsn_code: e.target.value }]
                        })}
                        placeholder="998314"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newInvoice.items[0].quantity}
                        onChange={(e) => setNewInvoice({
                          ...newInvoice,
                          items: [{ ...newInvoice.items[0], quantity: parseInt(e.target.value) || 1 }]
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Unit Price (excl. GST)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newInvoice.items[0].unit_price}
                        onChange={(e) => setNewInvoice({
                          ...newInvoice,
                          items: [{ ...newInvoice.items[0], unit_price: parseFloat(e.target.value) || 0 }]
                        })}
                        placeholder="0.00"
                        data-testid="item-price"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={createInvoice} 
                  className="w-full" 
                  disabled={creatingInvoice}
                  data-testid="submit-invoice-btn"
                >
                  {creatingInvoice ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Receipt className="h-4 w-4 mr-2" />
                  )}
                  Create Invoice
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Receipt className="h-4 w-4" />
              Total Invoices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats?.total_invoices || 0}</p>
            <p className="text-xs text-slate-500">{stats?.period}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Building2 className="h-4 w-4" />
              B2B Invoices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600">{stats?.b2b_count || 0}</p>
            <p className="text-xs text-slate-500">With GSTIN</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <User className="h-4 w-4" />
              B2C Invoices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{stats?.b2c_count || 0}</p>
            <p className="text-xs text-slate-500">Without GSTIN</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />
              Total Revenue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(stats?.total_revenue)}
            </p>
            <p className="text-xs text-slate-500">GST: {formatCurrency((stats?.total_cgst || 0) + (stats?.total_sgst || 0) + (stats?.total_igst || 0))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Reports Section - Only for reporting user */}
      {isReportingUser ? (
        <Card data-testid="reports-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Generate GST Reports
            </CardTitle>
            <CardDescription>Download GSTR-1 format reports for filing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={reportFromDate}
                  onChange={(e) => setReportFromDate(e.target.value)}
                  className="w-40"
                  data-testid="report-from-date"
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={reportToDate}
                  onChange={(e) => setReportToDate(e.target.value)}
                  className="w-40"
                  data-testid="report-to-date"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => downloadReport('b2b')}
                disabled={generatingReport}
                data-testid="download-b2b-report"
              >
                <Download className="h-4 w-4 mr-2" />
                B2B Report
              </Button>
              <Button
                variant="outline"
                onClick={() => downloadReport('b2c')}
                disabled={generatingReport}
                data-testid="download-b2c-report"
              >
                <Download className="h-4 w-4 mr-2" />
                B2C Report
              </Button>
              <Button
                onClick={() => downloadReport('all')}
                disabled={generatingReport}
                data-testid="download-all-report"
              >
                {generatingReport ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                All Invoices
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-amber-200 bg-amber-50" data-testid="restricted-notice">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Limited Access</p>
                <p className="text-sm text-amber-700">
                  Full GST reports are restricted. You can create invoices and view recent data (last 7 days).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle>Invoices</CardTitle>
              <CardDescription>
                {isReportingUser ? 'All invoices' : 'Recent invoices (last 7 days)'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={invoiceType} onValueChange={setInvoiceType}>
                <SelectTrigger className="w-32" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="B2B">B2B Only</SelectItem>
                  <SelectItem value="B2C">B2C Only</SelectItem>
                </SelectContent>
              </Select>
              {isReportingUser && (
                <>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-36"
                    placeholder="From"
                  />
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-36"
                    placeholder="To"
                  />
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No invoices found</p>
              <p className="text-sm">Create your first invoice to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Taxable</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                      <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                      <TableCell>{inv.invoice_date}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{inv.customer_name}</p>
                          {inv.customer_gstin && (
                            <p className="text-xs text-slate-500">{inv.customer_gstin}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={inv.invoice_type === 'B2B' ? 'default' : 'secondary'}>
                          {inv.invoice_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(inv.taxable_value)}</TableCell>
                      <TableCell className="text-right">
                        <div>
                          <p>{formatCurrency(inv.total_gst)}</p>
                          <p className="text-xs text-slate-500">
                            {inv.supply_type === 'inter' 
                              ? `IGST: ${formatCurrency(inv.igst_amount)}`
                              : `C: ${formatCurrency(inv.cgst_amount)} S: ${formatCurrency(inv.sgst_amount)}`
                            }
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(inv.total_amount)}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={
                            inv.status === 'paid' ? 'border-green-500 text-green-600' :
                            inv.status === 'cancelled' ? 'border-red-500 text-red-600' :
                            'border-blue-500 text-blue-600'
                          }
                        >
                          {inv.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
